'use client'

/**
 * Channel detail v3 — Bill prototype 2026-05-30 (.ch2 + .chhdr + .ovmetrics).
 *
 * One route renders all four launched channels (search, social, tv,
 * programmatic). Catalog data arrives from the Server Component; metrics
 * are loaded client-side from /api/connectors/catalog/metrics so anon
 * visitors / first-paint don't block on the rollup.
 *
 * Layout (top → bottom):
 *   • Eyebrow                 → channel name + meta on the right
 *   • Header                  → tile + h1 + sub + date range + live pill + Run command
 *   • KPI band                → four KPI tiles (Impressions / Spend / ROAS / Revenue)
 *   • Two-column area         → spend-over-time line chart + connector performance bars
 *   • Connector cards         → one card per subchannel
 *   • Campaigns + creatives tabs
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { subDays } from 'date-fns'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import {
  ZapIcon,
  ChartLineData01Icon,
  DollarCircleIcon,
  Target02Icon,
  EyeIcon,
  ArrowDataTransferHorizontalIcon,
  Cancel01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { getChannelIcon } from '@/lib/budget/channel-icons'
import { getChannelIconSolid } from '@/lib/channels/icons'
import type {
  CatalogChannelView,
  CatalogSubchannelView,
} from '@/lib/connectors/get-catalog'
import { sortSubchannelsForGrid } from '@/lib/connectors/sort-subchannels-for-grid'
import { applyVirtualSubchannelsToChannel } from '@/lib/connectors/virtual-subchannels'
import { SubchannelCard } from '@/components/marketplace/SubchannelCard'
import { DateRangeFilter, type DateRangeValue } from '@/components/shared/DateRangeFilter'
import { CampaignsTabs } from '@/components/campaigns/CampaignsTabs'
import { SetGoalsModal } from './SetGoalsModal'
import { SettingsConnectionPanel } from '@/components/settings/SettingsConnectionPanel'
import {
  FALLBACK_CAMPAIGNS,
  type CampaignChannelKey,
  type CampaignRow as CampaignTabsRow,
  type CreativeRow,
} from '@/lib/campaigns/fallback-data'
import { FALLBACK_CREATIVES as FALLBACK_CHANNEL_CREATIVES } from '@/lib/creatives/fallback-data'
import {
  DEMO_CHANNELS,
  DEMO_SUBCHANNELS,
  demoChannelCompareSeries,
  type DemoChannelKey,
} from '@/lib/fixtures/demo-brand'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { cn } from '@/lib/utils'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { DemoDataBadge } from '@/components/DemoDataBadge'
import { GuestAuthDialog } from '@/components/GuestAuthDialog'

// Per-channel v3 palette — reads Bill's globals.css tokens (--color-ch-*,
// --tile-*) so this surface stays in sync with the marketplace cards and
// budget allocation rows that share the same palette. Hex values are
// duplicated here only because recharts needs a literal string for stroke;
// CSS var resolution doesn't work inside the chart's React props.
const CHANNEL_HEX: Record<string, string> = {
  search: '#A8780C',
  social: '#2D6CDF',
  tv: '#7A5BD6',
  programmatic: '#0A8A6E',
  audio: '#E0762D',
  retail: '#C2185B',
}
function channelHex(key: string): string {
  return CHANNEL_HEX[key] ?? '#1A1A1A'
}

const CHANNEL_CLASSES: Record<string, {
  bg: string
  fill: string
  tile: string
  tintBg: string
  tintText: string
  borderLeft: string
}> = {
  search: {
    bg: 'bg-ch-search',
    fill: 'fill-ch-search',
    tile: 'bg-[image:var(--tile-search)]',
    tintBg: 'bg-ch-search-tint',
    tintText: 'text-ch-search-ink',
    borderLeft: 'border-l-ch-search',
  },
  social: {
    bg: 'bg-ch-social',
    fill: 'fill-ch-social',
    tile: 'bg-[image:var(--tile-social)]',
    tintBg: 'bg-ch-social-tint',
    tintText: 'text-ch-social-ink',
    borderLeft: 'border-l-ch-social',
  },
  tv: {
    bg: 'bg-ch-tv',
    fill: 'fill-ch-tv',
    tile: 'bg-[image:var(--tile-tv)]',
    tintBg: 'bg-ch-tv-tint',
    tintText: 'text-ch-tv-ink',
    borderLeft: 'border-l-ch-tv',
  },
  programmatic: {
    bg: 'bg-ch-programmatic',
    fill: 'fill-ch-programmatic',
    tile: 'bg-[image:var(--tile-programmatic)]',
    tintBg: 'bg-ch-programmatic-tint',
    tintText: 'text-ch-programmatic-ink',
    borderLeft: 'border-l-ch-programmatic',
  },
  audio: {
    bg: 'bg-ch-audio',
    fill: 'fill-ch-audio',
    tile: 'bg-[image:var(--tile-audio)]',
    tintBg: 'bg-ch-audio-tint',
    tintText: 'text-ch-audio-ink',
    borderLeft: 'border-l-ch-audio',
  },
  retail: {
    bg: 'bg-ch-retail',
    fill: 'fill-ch-retail',
    tile: 'bg-[image:var(--tile-retail)]',
    tintBg: 'bg-ch-retail-tint',
    tintText: 'text-ch-retail-ink',
    borderLeft: 'border-l-ch-retail',
  },
}
function channelClasses(key: string) {
  return CHANNEL_CLASSES[key] ?? {
    bg: 'bg-brand',
    fill: 'fill-brand',
    tile: 'bg-[image:var(--tile-brand)]',
    tintBg: 'bg-brand-tint',
    tintText: 'text-brand-mid',
    borderLeft: 'border-l-brand',
  }
}

// /api/connectors/catalog/metrics response shape — re-typed locally so
// this component doesn't import a server-route's type module directly.
interface ChannelMetricsItem {
  channel_key: string
  spend_cents: number
  impressions: number
  clicks: number
  conversions: number
  purchase_revenue_cents: number
  ctr: number | null
  roas: number | null
}
interface SubchannelMetricsItem {
  subchannel_key: string
  channel_key: string
  spend_cents: number
  impressions: number
  clicks: number
  conversions: number
  purchase_revenue_cents: number
  ctr: number | null
  roas: number | null
}
interface MetricsResponse {
  window_start: string
  by_channel: ChannelMetricsItem[]
  by_subchannel: SubchannelMetricsItem[]
}
type CompareRange = 'wow' | 'mom' | 'yoy' | 'all'
const COMPARE_TABS: { value: CompareRange; label: string }[] = [
  { value: 'wow', label: 'Week over Week' },
  { value: 'mom', label: 'Month over Month' },
  { value: 'yoy', label: 'Year over Year' },
  { value: 'all', label: 'All time' },
]
interface CompareSeriesPoint {
  offset: number
  date: string
  spend_cents: number
}
interface CompareResponse {
  channel_key: string
  range: CompareRange
  current: { from: string; to: string; series: CompareSeriesPoint[] }
  prior: { from: string; to: string; series: CompareSeriesPoint[] } | null
}
interface CampaignsResponse {
  campaigns: CampaignTabsRow[]
}
interface CreativesResponse {
  creatives: unknown[]
}

// Channel descriptive blurb — Bill's CHANNELDATA[k].sub copy.
const CHANNEL_BLURBS: Record<string, string> = {
  search: 'Intent-driven demand capture across search engines and marketplaces.',
  social: 'Paid social across feeds, stories, and creator placements.',
  tv: 'Premium streaming and linear TV — the living-room screen.',
  programmatic: 'Open-web display, video, and CTV bought across DSPs.',
  audio: 'Streaming and broadcast audio — Spotify, Pandora, iHeart, SiriusXM, Audacy.',
  retail: 'Retail media networks — Amazon, Walmart, Target, Instacart, Kroger.',
}

function fmtCents(cents: number): string {
  if (cents === 0) return '$0'
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
function fmtImpr(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return n.toLocaleString()
}
function toDateParam(date: Date): string {
  return date.toISOString().slice(0, 10)
}
function rangeForApi(preset: DateRangeValue['preset']): '7d' | '30d' | '90d' {
  if (preset === 'last_7') return '7d'
  if (preset === 'last_90') return '90d'
  return '30d'
}
function adaptCreativeForTabs(row: unknown): CreativeRow {
  const creative = row as {
    kind?: string | null
    display_name?: string | null
    name?: string | null
  }
  const kind = creative.kind ?? ''
  return {
    name: creative.display_name || creative.name || 'Untitled creative',
    channel: kind === 'rsa'
      ? 'search'
      : kind === 'video'
        ? 'tv'
        : kind === 'audio'
          ? 'audio'
          : 'programmatic',
    format: kind === 'video'
      ? 'Video'
      : kind === 'rsa'
        ? 'Text'
        : 'Image',
  }
}

function normalizeSubchannelName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function matchDemoSubchannelsToCatalog(channelKey: DemoChannelKey, catalog: CatalogSubchannelView[]) {
  const available = [...catalog]
  return DEMO_SUBCHANNELS
    .filter((demo) => demo.channel === channelKey)
    .map((demo) => {
      const demoKey = normalizeSubchannelName(demo.key)
      const demoName = normalizeSubchannelName(demo.name)
      const index = available.findIndex((sub) => {
        const subKey = normalizeSubchannelName(sub.key)
        const subName = normalizeSubchannelName(sub.name)
        return (
          subKey.includes(demoKey) ||
          demoKey.includes(subKey) ||
          subName.includes(demoName) ||
          demoName.includes(subName)
        )
      })
      if (index === -1) return null
      const [catalogSub] = available.splice(index, 1)
      return { demo, catalog: catalogSub }
    })
    .filter((item): item is { demo: (typeof DEMO_SUBCHANNELS)[number]; catalog: CatalogSubchannelView } => !!item)
}

export function ChannelDetailPage({
  channel: channelProp,
  isLoggedIn,
  workspaceId,
}: {
  channel: CatalogChannelView
  isLoggedIn: boolean
  workspaceId: string | null
}) {
  const router = useRouter()
  const channel = useMemo(() => applyVirtualSubchannelsToChannel(channelProp), [channelProp])
  const chanHex = channelHex(channel.key)
  const chanClass = channelClasses(channel.key)
  const icon = getChannelIcon(channel.key)
  const headerIcon = getChannelIconSolid(channel.key) ?? icon
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({
    preset: 'last_30',
    from: subDays(new Date(), 30),
    to: new Date(),
  }))
  const fromParam = toDateParam(dateRange.from)
  const toParam = toDateParam(dateRange.to)
  const apiRange = rangeForApi(dateRange.preset)

  const visibleConnectorSubs = useMemo(
    () =>
      sortSubchannelsForGrid(
        channel.subchannels.filter((s) => s.status !== 'deprecated'),
      ),
    [channel.subchannels],
  )
  const demoChannel = DEMO_CHANNELS[channel.key as DemoChannelKey]
  const liveSubs = useMemo(() => {
    if (isLoggedIn) {
      return channel.subchannels.filter(
        (s) => s.connection?.status === 'connected' || s.connection?.status === 'pending',
      )
    }
    return visibleConnectorSubs.slice(0, demoChannel?.liveConnectors ?? 0)
  }, [channel.subchannels, demoChannel?.liveConnectors, isLoggedIn, visibleConnectorSubs])

  // Metrics: per-channel rollup + per-subchannel breakdown.
  const [metrics, setMetrics] = useState<ChannelMetricsItem | null>(null)
  const [subMetrics, setSubMetrics] = useState<Record<string, SubchannelMetricsItem>>({})
  const [metricsLoading, setMetricsLoading] = useState(true)
  const [compareRange, setCompareRange] = useState<CompareRange>('mom')
  const [compareData, setCompareData] = useState<CompareResponse | null>(null)
  const [compareLoading, setCompareLoading] = useState(true)
  // Comparison overlay (WoW/MoM/YoY/All-time prior series). Off by
  // default — users opt in when they want to see deltas.
  const [showComparison, setShowComparison] = useState(false)
  const [campaigns, setCampaigns] = useState<CampaignTabsRow[]>([])
  const [creatives, setCreatives] = useState<CreativeRow[]>([])
  const [goalsModalOpen, setGoalsModalOpen] = useState(false)
  const [openSubchannel, setOpenSubchannel] = useState<CatalogSubchannelView | null>(null)
  const [showGuestAuth, setShowGuestAuth] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!isLoggedIn) {
      const demo = DEMO_CHANNELS[channel.key as DemoChannelKey]
      if (demo) {
        setMetrics({
          channel_key: demo.key,
          spend_cents: demo.spendCents,
          impressions: DEMO_SUBCHANNELS
            .filter((sub) => sub.channel === demo.key)
            .reduce((sum, sub) => sum + Math.round((sub.spendCents / Math.max(1, demo.spendCents)) * 2_200_000), 0),
          clicks: 0,
          conversions: 0,
          purchase_revenue_cents: Math.round(demo.spendCents * demo.roas),
          ctr: null,
          roas: demo.roas > 0 ? demo.roas : null,
        })
        const bySub: Record<string, SubchannelMetricsItem> = {}
        for (const item of matchDemoSubchannelsToCatalog(demo.key, visibleConnectorSubs)) {
          bySub[item.catalog.key] = {
            subchannel_key: item.catalog.key,
            channel_key: demo.key,
            spend_cents: item.demo.spendCents,
            impressions: Math.round((item.demo.spendCents / Math.max(1, demo.spendCents)) * 2_200_000),
            clicks: 0,
            conversions: 0,
            purchase_revenue_cents: Math.round(item.demo.spendCents * item.demo.roas),
            ctr: null,
            roas: item.demo.roas,
          }
        }
        setSubMetrics(bySub)
      }
      setMetricsLoading(false)
      return () => {
        cancelled = true
      }
    }
    setMetricsLoading(true)
    void (async () => {
      try {
        // TODO: API ignores from/to today; PR-E (follow-up) wires period support.
        const params = new URLSearchParams({
          channel: channel.key,
          range: apiRange,
          from: fromParam,
          to: toParam,
        })
        const res = await fetch(`/api/connectors/catalog/metrics?${params.toString()}`)
        if (!res.ok) return
        const data = (await res.json()) as MetricsResponse
        if (cancelled) return
        const ch = data.by_channel.find((r) => r.channel_key === channel.key) ?? null
        setMetrics(ch)
        const subMap: Record<string, SubchannelMetricsItem> = {}
        for (const row of data.by_subchannel) {
          if (row.channel_key === channel.key) subMap[row.subchannel_key] = row
        }
        setSubMetrics(subMap)
      } catch {
        // non-fatal — KPIs stay at their fallback empty state
      } finally {
        if (!cancelled) setMetricsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiRange, channel.key, fromParam, isLoggedIn, toParam, visibleConnectorSubs])

  useEffect(() => {
    let cancelled = false
    if (!isLoggedIn) {
      const demo = DEMO_CHANNELS[channel.key as DemoChannelKey]
      if (demo) {
        const compare = demoChannelCompareSeries(demo.key, compareRange === 'wow' ? 7 : compareRange === 'all' ? 90 : 30)
        setCompareData({
          channel_key: demo.key,
          range: compareRange,
          current: {
            from: compare.current.from,
            to: compare.current.to,
            series: compare.current.series.map((point) => ({
              offset: point.offset,
              date: point.current_date,
              spend_cents: point.spend_cents,
            })),
          },
          prior: compare.prior
            ? {
                from: compare.prior.from,
                to: compare.prior.to,
                series: compare.prior.series.map((point) => ({
                  offset: point.offset,
                  date: point.prior_date,
                  spend_cents: point.spend_cents,
                })),
              }
            : null,
        })
      } else {
        setCompareData(null)
      }
      setCompareLoading(false)
      return () => {
        cancelled = true
      }
    }
    setCompareLoading(true)
    fetch(`/api/channels/${encodeURIComponent(channel.key)}/spend-timeseries?range=${compareRange}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: CompareResponse | null) => {
        if (!cancelled) setCompareData(d)
      })
      .catch(() => {
        if (!cancelled) setCompareData(null)
      })
      .finally(() => {
        if (!cancelled) setCompareLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [channel.key, compareRange, isLoggedIn])

  useEffect(() => {
    let cancelled = false
    if (!isLoggedIn || !workspaceId) {
      setCampaigns(
        FALLBACK_CAMPAIGNS.filter((row) => row.channel === channel.key),
      )
      setCreatives(
        FALLBACK_CHANNEL_CREATIVES.filter(
          (row) => row.channel === channel.key || row.channel === 'any',
        ).map((row) => ({
          name: row.name,
          channel: row.channel === 'any' ? channel.key as CampaignChannelKey : row.channel,
          format: row.format,
        })),
      )
      return () => {
        cancelled = true
      }
    }

    void (async () => {
      try {
        // TODO: API ignores from/to today; PR-E (follow-up) wires period support.
        const params = new URLSearchParams({
          channel: channel.key,
          range: apiRange,
          from: fromParam,
          to: toParam,
          limit: '100',
        })
        const [campaignsRes, creativesRes] = await Promise.all([
          fetch(`/api/campaigns?${params.toString()}`),
          fetch(`/api/creatives?${params.toString()}`),
        ])
        if (!campaignsRes.ok) throw new Error('Failed to load campaigns')
        const campaignsData = (await campaignsRes.json()) as CampaignsResponse
        const creativesData = creativesRes.ok
          ? (await creativesRes.json()) as CreativesResponse
          : { creatives: [] }
        if (!cancelled) {
          setCampaigns(campaignsData.campaigns ?? [])
          setCreatives((creativesData.creatives ?? []).map(adaptCreativeForTabs))
        }
      } catch {
        if (!cancelled) {
          setCampaigns([])
          setCreatives([])
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiRange, channel.key, fromParam, isLoggedIn, toParam, workspaceId])

  const spend = metrics?.spend_cents ?? 0
  const impressions = metrics?.impressions ?? 0
  const roas = metrics?.roas
  const revenue = metrics?.purchase_revenue_cents ?? 0

  const chartData = useMemo(() => {
    if (!compareData) return []
    const currentByOffset = new Map(compareData.current.series.map((p) => [p.offset, p]))
    const priorByOffset = compareData.prior
      ? new Map(compareData.prior.series.map((p) => [p.offset, p]))
      : null
    const maxLen = Math.max(
      compareData.current.series.length,
      compareData.prior?.series.length ?? 0,
    )
    return Array.from({ length: maxLen }, (_, i) => ({
      offset: i,
      current_cents: currentByOffset.get(i)?.spend_cents ?? null,
      current_date: currentByOffset.get(i)?.date ?? null,
      prior_cents: priorByOffset?.get(i)?.spend_cents ?? null,
      prior_date: priorByOffset?.get(i)?.date ?? null,
    }))
  }, [compareData])

  const perfRows = useMemo(() => {
    const rows = liveSubs.map((sub) => {
      const m = subMetrics[sub.key]
      return {
        sub,
        spend_cents: m?.spend_cents ?? 0,
        roas: m?.roas ?? null,
      }
    })
    const maxRoas = Math.max(1, ...rows.map((r) => r.roas ?? 0))
    return { rows, maxRoas }
  }, [liveSubs, subMetrics])

  const prettyName = channel.name
  const channelKeyForToken = channel.key
  const channelFilter = channel.key as Exclude<CampaignChannelKey, 'other'>
  const isComingSoon = channel.key === 'retail'
  const handleOpenSubchannel = (sub: CatalogSubchannelView) => {
    if (!isLoggedIn) {
      setShowGuestAuth(true)
      return
    }
    setOpenSubchannel(sub)
  }

  return (
    <div className="min-h-full bg-canvas">
      <LobbyHeaderMeta>
        {liveSubs.length} of {channel.subchannels.length} connectors live
      </LobbyHeaderMeta>

      <div className="m-0 max-w-none px-4 pb-[70px] pt-6 sm:px-8">
        {/* Header */}
        <div className="mb-1 flex flex-wrap items-center gap-4 pt-1.5">
          <div
            className={cn(
              'flex size-[52px] shrink-0 items-center justify-center rounded-card text-white',
              chanClass.tile,
            )}
            aria-hidden="true"
          >
            <HugeiconsIcon icon={headerIcon} className="size-6" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="m-0 text-[30px] font-extrabold tracking-[-0.02em] text-text-strong">{prettyName}</h1>
              {!isLoggedIn && <DemoDataBadge />}
            </div>
            <div className="mt-[3px] max-w-[540px] text-[13.5px] text-text">
              {CHANNEL_BLURBS[channelKeyForToken] ?? channel.description ?? ''}
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-[9px] sm:ml-auto sm:w-auto">
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
            <span
              className={cn(
                'rounded-pill px-3 py-1.5 font-mono text-xs font-bold',
                chanClass.tintBg,
                chanClass.tintText,
              )}
            >
              {liveSubs.length} / {channel.subchannels.length} live
            </span>
            <button
              type="button"
              onClick={() => router.push('/commands/new')}
              className="inline-flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-ctrl border border-line-2 bg-surface px-3.5 text-[13px] font-bold text-text-strong no-underline hover:bg-canvas"
            >
              <HugeiconsIcon icon={ZapIcon} className="size-4" />
              Run a command
            </button>
            <button
              type="button"
              onClick={() => setGoalsModalOpen(true)}
              className="inline-flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-ctrl border border-line-2 bg-surface px-3.5 text-[13px] font-bold text-text-strong no-underline hover:bg-canvas"
            >
              <HugeiconsIcon icon={Target02Icon} className="size-4" />
              Set goals
            </button>
          </div>
        </div>

        {isComingSoon ? (
          <ComingSoonPanel channelName={prettyName} channelIcon={headerIcon} />
        ) : (
          <>
            {/* TODO: wire real prev-period delta in a follow-up PR. */}
            <div className="mt-[18px] grid grid-cols-4 gap-3 max-[900px]:grid-cols-2 max-[460px]:grid-cols-1">
              <KpiTile
                label="Impressions"
                value={metricsLoading ? '—' : fmtImpr(impressions)}
                iconNode={<HugeiconsIcon icon={EyeIcon} className="size-4" />}
              />
              <KpiTile
                label="Spend"
                value={metricsLoading ? '—' : fmtCents(spend)}
                iconNode={<HugeiconsIcon icon={DollarCircleIcon} className="size-4" />}
              />
              <KpiTile
                label="Blended ROAS"
                value={metricsLoading ? '—' : roas != null ? `${roas.toFixed(1)}×` : '—'}
                iconNode={<HugeiconsIcon icon={Target02Icon} className="size-4" />}
              />
              <KpiTile
                label="Attributed revenue"
                value={metricsLoading ? '—' : fmtCents(revenue)}
                iconNode={<HugeiconsIcon icon={ChartLineData01Icon} className="size-4" />}
              />
            </div>

            {/* Two-column: spend chart + connector performance */}
            <div className="mt-3.5 grid grid-cols-[1.55fr_1fr] gap-3 max-[900px]:grid-cols-1">
              <div className="rounded-card border-[0.5px] border-line bg-surface px-[18px] py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold text-text">Spend over time</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setShowComparison((v) => !v)}
                      aria-pressed={showComparison}
                      title={showComparison ? 'Stop comparing' : 'Compare to prior period'}
                      className={cn(
                        'inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-1 font-mono text-[11px] transition-colors',
                        showComparison
                          ? 'border-transparent text-text-strong shadow-sm'
                          : 'border-line bg-canvas text-text-muted-2 hover:text-text',
                      )}
                      style={showComparison ? { backgroundColor: `${chanHex}22` } : undefined}
                    >
                      <HugeiconsIcon
                        icon={showComparison ? Cancel01Icon : ArrowDataTransferHorizontalIcon}
                        className="size-3"
                      />
                      {showComparison ? 'Stop comparing' : 'Compare'}
                    </button>
                    {showComparison && (
                      <div className="flex flex-wrap items-center gap-0.5 overflow-x-auto rounded-pill border border-line-2 bg-canvas p-0.5">
                        {COMPARE_TABS.map((tab) => (
                          <button
                            key={tab.value}
                            type="button"
                            onClick={() => setCompareRange(tab.value)}
                            className={cn(
                              'whitespace-nowrap rounded-pill px-2.5 py-1 font-mono text-[11px] transition-colors',
                              compareRange === tab.value
                                ? cn(chanClass.tintBg, chanClass.tintText, 'shadow-sm')
                                : 'text-text-muted-2 hover:text-text',
                            )}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative mt-2.5 h-[172px]">
                  {compareLoading ? (
                    <div className="grid h-full place-items-center text-[13px] text-text-muted-2">
                      Loading…
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 4, left: -10 }}>
                        <CartesianGrid stroke="var(--color-line-subtle)" vertical={false} />
                        <XAxis
                          dataKey="offset"
                          tick={{ fill: 'var(--color-text-muted-2)', fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => `Day ${Number(v) + 1}`}
                        />
                        <YAxis
                          tick={{ fill: 'var(--color-text-muted-2)', fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => {
                            const n = Number(v) / 100
                            return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`
                          }}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null
                            const cur = payload.find((p) => p.dataKey === 'current_cents')?.value as number | null
                            const pri = payload.find((p) => p.dataKey === 'prior_cents')?.value as number | null
                            const curD = Number(cur ?? 0) / 100
                            const priD = Number(pri ?? 0) / 100
                            const delta = pri != null && pri > 0 ? ((curD - priD) / priD) * 100 : null
                            const point = chartData[Number(label)]
                            const curDate = point?.current_date
                            const priDate = point?.prior_date
                            return (
                              <div className="rounded-md border-[0.5px] border-line bg-surface px-3 py-2 text-[12px] shadow-sm">
                                <div className="mb-1 font-mono text-text-muted-2">Day {Number(label) + 1}</div>
                                <div className="flex items-center gap-2">
                                  <span className="size-2 rounded-full" style={{ background: chanHex }} />
                                  <span className="text-text">Current{curDate ? ` (${curDate})` : ''}:</span>
                                  <span className="font-mono text-text-strong">
                                    {cur != null ? `$${Math.round(curD).toLocaleString()}` : '—'}
                                  </span>
                                </div>
                                {showComparison && pri != null && (
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className="size-2 rounded-full border border-line-2" />
                                    <span className="text-text">Prior{priDate ? ` (${priDate})` : ''}:</span>
                                    <span className="font-mono text-text-muted">
                                      ${Math.round(priD).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                                {showComparison && delta != null && (
                                  <div className={cn('mt-1 font-mono text-[11px]', delta >= 0 ? 'text-brand-mid' : 'text-danger-ink')}>
                                    {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% vs prior
                                  </div>
                                )}
                              </div>
                            )
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="current_cents"
                          stroke={chanHex}
                          strokeWidth={2.5}
                          dot={false}
                          connectNulls
                        />
                        {showComparison && compareData?.prior && (
                          <Line
                            type="monotone"
                            dataKey="prior_cents"
                            stroke="var(--color-text-muted-2)"
                            strokeWidth={1.5}
                            strokeDasharray="4 3"
                            dot={false}
                            connectNulls
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
                {showComparison && compareData && (
                  <div className="mt-2 flex items-center gap-4 text-[11px] text-text-muted-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-0.5 w-4" style={{ background: chanHex }} />
                      Current ({compareData.current.from} → {compareData.current.to})
                    </span>
                    {compareData.prior && (
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="block h-0.5 w-4 border-t border-dashed"
                          style={{ borderColor: 'var(--color-text-muted-2)' }}
                        />
                        Prior ({compareData.prior.from} → {compareData.prior.to})
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border-[0.5px] border-line bg-surface px-5 py-[18px]">
                <div className="mb-4 text-[15px] font-bold text-text-strong">Connector performance</div>
                {liveSubs.length === 0 ? (
                  <div className="text-[13px] text-text-muted">No live connectors yet.</div>
                ) : (
                  perfRows.rows.map((r) => (
                    <div key={r.sub.key} className="mb-[15px] flex items-center gap-3 last:mb-0">
                      <span className="w-[104px] shrink-0 truncate whitespace-nowrap text-[13px] font-semibold text-text-strong">{r.sub.name}</span>
                      <span className="w-[58px] shrink-0 text-right font-mono text-xs text-text-muted-2">{fmtCents(r.spend_cents)}</span>
                      <span className="h-2 flex-1 overflow-hidden rounded-pill bg-surface-2">
                        <svg className="block size-full" preserveAspectRatio="none" viewBox="0 0 100 8" aria-hidden="true">
                          <rect
                            width={Math.min(100, Math.round(((r.roas ?? 0) / perfRows.maxRoas) * 100))}
                            height="8"
                            rx="4"
                            className={chanClass.fill}
                          />
                        </svg>
                      </span>
                      <span className="w-10 shrink-0 text-right font-mono text-[13px] font-extrabold text-text-strong">{r.roas != null ? `${r.roas.toFixed(1)}×` : '—'}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Connector cards */}
            <div className="mb-3 mt-[26px] flex items-center gap-[9px] text-[15px] font-bold text-text-strong">
              Connectors <span className="rounded-[7px] bg-text-strong px-2 py-px font-mono text-xs text-white dark:text-atrium-background">{visibleConnectorSubs.length}</span>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(244px,1fr))] gap-3">
              {visibleConnectorSubs.map((sub) => (
                <SubchannelCard
                  key={sub.key}
                  sub={sub}
                  channelKey={channel.key}
                  onOpen={handleOpenSubchannel}
                />
              ))}
            </div>

            {/* Campaigns + Creatives — reused from /campaigns, pre-filtered to this channel */}
            <div className="mt-5">
              <CampaignsTabs
                campaigns={campaigns}
                creatives={creatives}
                channelFilter={channelFilter}
              />
            </div>
          </>
        )}
      </div>
      <SetGoalsModal
        channelKey={channel.key}
        channelName={prettyName}
        open={goalsModalOpen}
        onOpenChange={setGoalsModalOpen}
      />
      <SettingsConnectionPanel
        subchannel={openSubchannel}
        channelAccentHex={channel.accent_hex}
        channelName={channel.name}
        onClose={() => setOpenSubchannel(null)}
        onChanged={() => router.refresh()}
      />
      <GuestAuthDialog
        open={showGuestAuth}
        onOpenChange={setShowGuestAuth}
        heading="Sign in to Atrium"
        body="Create a free account or sign in to connect your ad platforms and start running campaigns."
      />
    </div>
  )
}

function KpiTile({
  label,
  value,
  iconNode,
}: {
  label: string
  value: string
  iconNode: ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-card border-[0.5px] border-line bg-surface pb-[15px]">
      <div className="flex items-center gap-1.5 px-4 pt-3.5 text-[13px] font-medium text-text">
        {iconNode}
        {label}
      </div>
      <div className="px-4 pt-2 font-mono text-[30px] font-extrabold tracking-[-0.04em] text-text-strong tabular-nums">{value}</div>
    </div>
  )
}

function ComingSoonPanel({
  channelName,
  channelIcon,
}: {
  channelName: string
  channelIcon: IconSvgElement
}) {
  return (
    <div className="mt-8 flex min-h-[420px] flex-col items-center justify-center rounded-card border-[0.5px] border-line bg-surface px-6 py-16 text-center">
      <div className="flex size-14 items-center justify-center rounded-card bg-canvas text-text-muted-2">
        <HugeiconsIcon icon={channelIcon} className="size-7" />
      </div>
      <h2 className="mt-5 text-[20px] font-bold text-text-strong">
        {channelName === 'Retail Media'
          ? 'Connect a retail integration to get started'
          : `${channelName} is coming soon`}
      </h2>
      <p className="mt-2 max-w-[440px] text-[14px] text-text-muted">
        {channelName === 'Retail Media'
          ? 'Retail has no demo spend yet. Connect Amazon Ads, Walmart Connect, Target Roundel, Instacart, or Kroger to start tracking retail media performance.'
          : `We're wiring up live data and connector integrations for ${channelName}. You'll be able to run campaigns, watch performance, and rebalance budget from this surface in a future release.`}
      </p>
    </div>
  )
}
